import {
  Box,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import StripeAlreadyPaidImg from "assets/images/integration/stripe/stripeAlreadyPaid.webp";
import { Error404Component } from "components";
import { useGetInvoicePaymentLink } from "controller/hooks/billing";
import { useEffect } from "react";
import type { PayNowErrorCode } from "shared";
import { useSearchParams } from "react-router-dom";

/** Only absolute http(s) links are followed - never a relative or `javascript:` URL. */
const isSafeRedirectUrl = (url: string) => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "https:" || parsed.protocol === "http:";
  } catch {
    return false;
  }
};

/**
 * Public landing route: `/pay-now?msp_id=<id>&invoice_id=<id>`.
 *
 * Linked from the invoice PDF / invoice email. Shows a loader while it
 * exchanges the two params for the latest payment link, then hands the payer
 * off to that link. Unauthenticated - it sits outside `SecureWrapper`.
 */
export const PayNowScreen = () => {
  const theme = useTheme();
  const [searchParams] = useSearchParams();

  const mspId = searchParams.get("msp_id") ?? "";
  const invoiceId = searchParams.get("invoice_id") ?? "";
  const hasParams = !!mspId && !!invoiceId;

  const { data, error, isError, isFetching, refetch } =
    useGetInvoicePaymentLink({
      mspId,
      invoiceId,
    });

  const paymentUrl = data?.url ?? "";
  const canRedirect = !!paymentUrl && isSafeRedirectUrl(paymentUrl);

  useEffect(() => {
    if (!canRedirect) {
      return;
    }
    // replace() so the browser back button returns to the invoice/email
    // instead of this loader, which would re-fire the request.
    window.location.replace(paymentUrl);
  }, [canRedirect, paymentUrl]);

  // A resolved-but-unusable link is as much a failure as a rejected request.
  const hasFailed = !hasParams || isError || (!!data && !canRedirect);

  // The failure reason as a stable code rather than an English string. The whole
  // error body is carried on `data` by `apiErrorHandler`, so `code` sits there
  // alongside `message`.
  const errorCode = error?.data?.code as PayNowErrorCode | undefined;

  // The API explains itself ("This invoice has not been issued yet", "Invoice
  // not found"), and those read better to a payer than a generic failure. Only
  // 4xx messages are shown — a 5xx message is server internals.
  const apiStatus = error?.status ?? 0;
  const apiMessage = apiStatus >= 400 && apiStatus < 500 ? error?.message : "";
  // Retrying only helps a transient failure. Every code but a Stripe-side
  // rejection states a settled fact about the invoice — voided, not issued,
  // already paid — and will answer the same way forever, so offering Retry
  // there only wastes the payer's time. No code at all means the request never
  // reached the API, which is worth retrying.
  const canRetry =
    hasParams && (!errorCode || errorCode === "PAYMENT_LINK_CREATION_FAILED");

  // Neither of these is a failure: the payer did nothing wrong and there is
  // nothing to fix. Kept off the error screen, which frames them as a fault and
  // shows a 404 illustration.
  if (
    !isFetching &&
    (errorCode === "INVOICE_ALREADY_PAID" ||
      errorCode === "INVOICE_ZERO_BALANCE")
  ) {
    return <PayNowNothingDueScreen code={errorCode} message={apiMessage} />;
  }

  if (hasFailed && !isFetching) {
    return (
      <Box sx={{ width: "100vw", height: "100vh" }}>
        <Error404Component
          title={
            apiMessage ||
            (hasParams
              ? "We couldn't generate your payment link."
              : "This payment link is incomplete.")
          }
          subtitle={
            apiMessage
              ? "Please contact the sender if you think this is wrong."
              : hasParams
                ? "Please try again."
                : "Please reopen it from your invoice or invoice email."
          }
          showCta={canRetry}
          ctaLabel="Retry"
          onCtaClick={() => refetch()}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: theme.palette.neutral["005"],
      }}
    >
      <Stack sx={{ alignItems: "center", gap: "16px" }}>
        <CircularProgress />
        <Typography
          variant="body-small"
          sx={{ color: theme.palette.lightBg.low }}
        >
          Generating payment link...
        </Typography>
      </Stack>
    </Box>
  );
};

/** Wording used when the API sends no message of its own. */
const NOTHING_DUE_FALLBACK: Record<
  "INVOICE_ALREADY_PAID" | "INVOICE_ZERO_BALANCE",
  string
> = {
  INVOICE_ALREADY_PAID: "This invoice has already been paid in full",
  INVOICE_ZERO_BALANCE: "This invoice has no balance due",
};

/**
 * Shown when there is nothing for the payer to pay.
 *
 * Two codes land here and they are not the same fact. `INVOICE_ALREADY_PAID`
 * means the invoice had a real total and someone settled it;
 * `INVOICE_ZERO_BALANCE` means it never owed anything at all. Saying "paid in
 * full" for the second would describe a payment that never happened, so the
 * heading differs by code even though the outcome is identical.
 *
 * The API's own message is preferred — it is the most specific description of
 * why this invoice cannot be paid, and it keeps the screen aligned with what
 * support sees in the logs. The wording above stands in when the response
 * carries no message.
 */
const PayNowNothingDueScreen = ({
  code,
  message,
}: {
  code: Extract<
    PayNowErrorCode,
    "INVOICE_ALREADY_PAID" | "INVOICE_ZERO_BALANCE"
  >;
  message?: string;
}) => {
  const theme = useTheme();
  const heading = message?.trim() || NOTHING_DUE_FALLBACK[code];
  return (
    <Stack
      sx={{
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "center",
        p: "24px",
      }}
    >
      <Stack sx={{ gap: "32px", alignItems: "center" }}>
        <img
          src={StripeAlreadyPaidImg}
          alt={
            code === "INVOICE_ALREADY_PAID"
              ? "Invoice already paid"
              : "Invoice has no balance due"
          }
          style={{ height: "140px", width: "auto" }}
        />
        <Stack sx={{ gap: "8px" }}>
          <Typography variant="h5" sx={{ textAlign: "center" }}>
            {heading}
          </Typography>
          <Typography
            variant="body-medium"
            sx={{ textAlign: "center", color: theme.palette.lightBg.low }}
          >
            Please contact the sender if you think this is wrong
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default PayNowScreen;
